import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IRefPhaserGame, PhaserGame } from 'Phaser/PhaserGame';
import { PhaserToReactListeners, NotifyPhaserEvents, RemoveListener } from 'Phaser/EventBus2';
import { useMidiContext, NoteMessage } from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';

/**
 * PianoDebug — calibration/demo page for the Debug Dummy MIDI feature
 * (FeatureFlag.DEBUG_DUMMY_MIDI). Mounts the shared Phaser game, starts the
 * stripped-down PianoDebugScene, and bridges notes from the MidiContext
 * pipeline (including the computer-keyboard injection) into the scene so a
 * developer can try out the keyboard → MIDI mapping and see keypress highlights.
 *
 * Gated behind the feature flag: if the flag is off we render nothing and
 * redirect back to the roadmap (defence-in-depth beside the flag-gated entry
 * point on the Roadmap page).
 */
const SCENE_KEY = 'PianoDebugScene';

const PianoDebug: React.FC = () => {
  const navigate = useNavigate();
  const enabled = useFeatureFlag(FeatureFlag.DEBUG_DUMMY_MIDI);
  const { addListener, removeListener } = useMidiContext();

  const phaserRef = React.useRef<IRefPhaserGame | null>(null);
  const gameReadyRef = React.useRef(false);
  const loadTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redirect away if the flag is disabled.
  React.useEffect(() => {
    if (!enabled) navigate('/roadmap');
  }, [enabled, navigate]);

  const loadScene = React.useCallback(() => {
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    const scene = phaserRef.current?.scene;
    const game = phaserRef.current?.game;

    // Wait for the Boot → Preloader sequence (or singleton reuse) to complete.
    if (!gameReadyRef.current || (!scene && !game)) {
      loadTimerRef.current = setTimeout(loadScene, 300);
      return;
    }

    const sceneIsActive = scene?.sys?.isActive() || scene?.sys?.isPaused();
    if (sceneIsActive && scene) {
      scene.scene.start(SCENE_KEY);
    } else if (game) {
      // Bypass ScenePlugin queuing when the ref scene is inactive/null.
      game.scene.stop(SCENE_KEY);
      game.scene.start(SCENE_KEY);
    } else {
      loadTimerRef.current = setTimeout(loadScene, 300);
    }
  }, []);

  // Boot the scene once the game is ready.
  React.useEffect(() => {
    if (!enabled) return;

    // A reused singleton game won't fire PhaserReady again — it's ready now.
    if (phaserRef.current?.wasReused) {
      gameReadyRef.current = true;
      loadScene();
    }

    PhaserToReactListeners.PhaserReady(() => {
      gameReadyRef.current = true;
      loadScene();
    });

    return () => {
      RemoveListener('PhaserReady');
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    };
  }, [enabled, loadScene]);

  // Bridge MidiContext note events (incl. the DEBUG_DUMMY_MIDI keyboard
  // injection) to the Phaser scene, mirroring MainAppPhaser's forwarding.
  React.useEffect(() => {
    if (!enabled) return;

    const listener = {
      name: 'piano-debug-bridge',
      noteOn: (ev: NoteMessage) => {
        if (ev.velocity === 0) {
          NotifyPhaserEvents.MidiOff({ note: ev.note });
          return;
        }
        NotifyPhaserEvents.MidiOn({ note: ev.note, velocity: ev.velocity });
      },
      noteOff: (ev: NoteMessage) => NotifyPhaserEvents.MidiOff({ note: ev.note }),
    };

    addListener(listener);
    return () => removeListener(listener);
  }, [enabled, addListener, removeListener]);

  if (!enabled) return null;

  return (
    <Box sx={{ position: 'relative', height: '100vh', width: '100vw', backgroundColor: '#0B1522' }}>
      <IconButton
        onClick={() => navigate('/roadmap')}
        aria-label="Back to roadmap"
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 2,
          color: '#ECECEC',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <PhaserGame ref={phaserRef} width={1920} height={1080} />
    </Box>
  );
};

export default PianoDebug;
